import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';

import { ProductUid } from '../related-products/domain/models/related-products.models';

import { ApiProduct } from '@api/_api/api-catalog/api-products/api-products.models';
import { Product, ProductColor, ProductSize } from '@api/catalog/products/products.models';
import { getCountryCode } from '@core/infrastructure/state/core.selectors';
import { getProductById } from '@features/productsArray/infrastructure/state/productsArray.selectors';
import { PublicSidebarCustomCommandProps, SidebarPublicModels, SidebarPublicPort } from '@features/sidebar';
import { DuttiDate, DuttiDateFormat } from '@frontend/common/duttidate';

@Injectable()
export class ProductUtilsService {
    constructor(
        private readonly store: Store,
    ) { }

    public static getMocacoCode(product: Product): string {
        let colorId;

        if (product.colors && product.colors.length > 0) {
            colorId = product.colors.find((color) => color.id === product.status.selectedColor.id)?.id;
        } else {
            colorId = product.status.selectedColor.id;
        }

        const partials = product.reference.split('-');

        const moca = partials[0] + colorId;

        return moca.substring(1, 11);
    }

    public static parseMocacoCode(product: ApiProduct | Product, color?: ProductColor): string {
        let colorId;
        if (color) {
            colorId = color.id;
        } else if (product?.status?.selectedColor) {
            colorId = product.status.selectedColor.id;
        } else {
            colorId = product.colors[0]?.id;
        }
        return `${product.moca}${colorId}`;
    }

    public static mapProductToProductUid(product): ProductUid {
        const productColors = product.colors || [];
        const productUid = {
            section: product.sectionNameEN ? product.sectionNameEN : undefined,
            product_id: product.id ? product.id : product.product_id,
            uid: product.id ? product.id : product.product_id,
            sku: product.sku ? product.sku : undefined, // para mocaco
            colorId: this.getProductColorId(product) || product.colorId,
            mocaId: product.mocaId,
            timestamp: product.timestamp ? product.timestamp : new Date().getTime(),
            arraySku: productColors.reduce((acc, curr) => {
                // eslint-disable-next-line no-param-reassign
                acc = acc.concat(curr.sizes.map((colorSize) => colorSize.sku));
                return acc;
            }, []),
        };

        return productUid;
    }

    public static getProductColorId(product) {
        if (product?.uid) {
            return product.uid.slice(-3);
        }
        if (product?.status?.selectedColor) {
            return product.status.selectedColor.id;
        }
        return undefined;
    }

    public static getAllMocacoCodes(product: Product): string[] {
        return product.colors.map((color) => {
            const partnumber = color.sizes[0].partnumber.split('-');
            const mocacoCode = partnumber[0].substring(1, partnumber[0].length - 2) + '-' + partnumber[1];
            return mocacoCode;
        });
    }

    public getProductsById$(ids: number[]): Observable<Product[]> {
        let select = ids.map((id) => {
            return this.store.select(getProductById(id)); // Importamos directamente el selector de la feature para no generar dependencia
        });

        return combineLatest(select);
    }

    public static checkIsComingSoonOrBackSoon(size: ProductSize): boolean {
        return (size.visibilityValue === 'COMING_SOON' || size.visibilityValue === 'BACK_SOON') && size.isBuyable;
    }

    public static checkCanShowSimilar(size: ProductSize): boolean {
        return size.visibilityValue === 'HIDDEN' || size.visibilityValue === 'SOLD_OUT';
    }

    public openComingSoonOrBackSoonModal(product: Product): void {
        this.store.select(getCountryCode).pipe(first()).subscribe((countryCode) => {
            const props: PublicSidebarCustomCommandProps = {
                component: SidebarPublicModels.sidebarCustomComponents.COMING_SOON,
                initializeFunction: undefined,
                data: {
                    product,
                    countryCode,
                },
            };
            SidebarPublicPort(this.store).openSidebarCustom(props);
        });
    }

    public openProductOutOfStockSidebar(baseProduct: Product) {
        const props: PublicSidebarCustomCommandProps = {
            component: SidebarPublicModels.sidebarCustomComponents.OUT_OF_STOCK,
            initializeFunction: undefined,
            data: { baseProduct },
        };
        SidebarPublicPort(this.store).openSidebarCustom(props);
    }

    public openNoStocksModals(product: Product, productSize?: ProductSize): void {
        const size = productSize || product.status.selectedSize;
        if (ProductUtilsService.checkIsComingSoonOrBackSoon(size)) {
            this.openComingSoonOrBackSoonModal(product);
        } else if (ProductUtilsService.checkCanShowSimilar(size)) {
            this.openProductOutOfStockSidebar(product);
        }
    }

    public static itemOutOfStock(product: Product, uniqueSize: ProductSize): boolean {
        if (product.colors?.length && product.status.selectedColor) {
            // eslint-disable-next-line no-secrets/no-secrets
            // Se podría simplificar la segunda condición => ['DISABLED', 'COMING_SOON', 'BACK_SOON'].includes(uniqueSize?.visibilityValue)
            return (
                product.status.selectedColor.sizes.length === 0
                || uniqueSize
                && (uniqueSize.visibilityValue === 'DISABLED'
                    || uniqueSize.visibilityValue === 'COMING_SOON'
                    || uniqueSize.visibilityValue === 'BACK_SOON')
            );
        }
        return false;
    }

    public static isAllProductSizesNotBuyable(sizes: ProductSize[], tags: string[], includeOrExclude: boolean = true) {
        return includeOrExclude
            ? sizes?.filter((size) => tags.includes(size.visibilityValue))
            : sizes?.filter((size) => !tags.includes(size.visibilityValue));
    }

    public static isProductSizeNotBuyable(sizes: ProductSize[], tags: string[], includeOrExclude: boolean = true): boolean {
        return sizes.some((s) => {
            const isVisible = includeOrExclude ? tags.includes(s?.visibilityValue) : !tags.includes(s?.visibilityValue);
            return isVisible;
        });
    }

    public redirectToWorldWideFn(availableFlag: boolean, product: Product, categoryId: string) {
        const props: PublicSidebarCustomCommandProps = {
            component: SidebarPublicModels.sidebarCustomComponents.PRODUCT_REDIRECT,
            initializeFunction: undefined,
            data: {
                product: product,
                available: availableFlag,
                categoryId: categoryId,
            },
        };
        SidebarPublicPort(this.store).openSidebarCustom(props);
    }

    public static getPartNumberColor(reference: string, color: string): string {
        const partNumber: string[] = reference?.split('-') ?? [];
        const colorId: string = color ?? '';
        return partNumber.join(colorId + '-');
    }

    public checkProductDateNew(productDate: string, configDays: number): boolean {
        const currentDate = new DuttiDate();

        const parts = productDate.split('.');
        const milliseconds = parts[1] ? parts[1].padEnd(3, '0') : '000';
        const formattedDate = `${parts[0]}.${milliseconds}`;
        const format = DuttiDateFormat.DAY_MONTH_YEAR_HOUR_MINUTE_SECOND_3MILLIS;
        const availabilityDate = new DuttiDate(
            formattedDate,
            format
        );

        const diffDays = currentDate.diff(availabilityDate, 'days');
        return !!diffDays ? diffDays <= configDays : false;
    }
}
